// Format of Play — shared components + helpers (Feb 2026).
//
// Provides:
//   * <FormatOfPlaySelector> — Admin picker used in event edit surfaces
//     (Tournament manage page + Round Robin manage page). Only visible
//     to admins; enforces the same "lock after any completed result"
//     rule that the backend enforces.
//   * <FormatAwareScoreFields> — Renders the score-entry fields for the
//     event's configured format. Callers pass the event's scoring_format,
//     games_target, tiebreak_to and receive back { scoreText, winnerEntryId,
//     valid, message } via the onChange callback.
//   * describeFormat(...) — human-readable label for a structured format.
//
// Both Power Set and Best-2-of-3 formats are supported. Legacy events
// (scoring_format == null) fall back to a plain score_text input — same
// behavior as before the migration.

import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';

export const FORMAT_OPTIONS = [
  { value: 'power_set',                 label: 'Power Set' },
  { value: 'best_of_3_match_tiebreak',  label: 'Best 2 of 3 Sets · 10-Pt 3rd-Set Tiebreak' },
];

export function describeFormat({ scoring_format, games_target, tiebreak_to, match_format_text }) {
  if (scoring_format === 'power_set') {
    return `Power Set to ${games_target || 8} · ${tiebreak_to || 7}-Point Tiebreak`;
  }
  if (scoring_format === 'best_of_3_match_tiebreak') {
    return `Best 2 of 3 Sets · 3rd Set ${tiebreak_to || 10}-Point Match Tiebreak`;
  }
  return match_format_text || '—';
}

/* ================================ Picker ================================ */

export function FormatOfPlaySelector({
  event, onSave, disabled, testidPrefix = 'fop',
}) {
  const scored = (event.matches || []).some((m) => m.status === 'completed' && m.winner_entry_id && m.score_text !== '(bye)');
  const [scoringFormat, setScoringFormat] = useState(event.scoring_format || '');
  const [gamesTarget, setGamesTarget] = useState(event.games_target ?? 8);
  const [tiebreakTo, setTiebreakTo] = useState(event.tiebreak_to ?? (event.scoring_format === 'best_of_3_match_tiebreak' ? 10 : 7));
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setScoringFormat(event.scoring_format || '');
    setGamesTarget(event.games_target ?? 8);
    setTiebreakTo(event.tiebreak_to ?? (event.scoring_format === 'best_of_3_match_tiebreak' ? 10 : 7));
  }, [event.id, event.scoring_format, event.games_target, event.tiebreak_to]);

  const locked = scored || disabled;

  const canSave = !!scoringFormat && !locked;

  const save = async () => {
    setBusy(true);
    try {
      const payload = { scoring_format: scoringFormat };
      if (scoringFormat === 'power_set') {
        payload.games_target = gamesTarget;
        payload.tiebreak_to = tiebreakTo;
      } else if (scoringFormat === 'best_of_3_match_tiebreak') {
        payload.games_target = null;
        payload.tiebreak_to = tiebreakTo;
      }
      await onSave(payload);
      toast.success('Format of Play saved.');
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : (d?.message || 'Save failed.'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      data-testid={`${testidPrefix}-root`}
      className="border border-[#e3dcd1] rounded-2xl bg-white p-4 sm:p-5 space-y-3"
    >
      <div className="flex items-center justify-between gap-3">
        <div>
          <div className="text-[11px] uppercase tracking-wider text-[#013523] font-semibold">Format of Play</div>
          <div className="text-[12px] text-[#6a766e] mt-1">
            Choose how each match is scored. The score-entry modal renders the correct fields based on this selection.
          </div>
        </div>
        {locked && (
          <span className="text-[10px] uppercase tracking-wider text-amber-800 bg-amber-50 border border-amber-200 rounded-full px-2 py-1" data-testid={`${testidPrefix}-locked`}>
            Locked
          </span>
        )}
      </div>

      <div className="grid gap-2 sm:grid-cols-2">
        {FORMAT_OPTIONS.map((opt) => (
          <label
            key={opt.value}
            className={`flex items-start gap-2 border rounded-xl p-3 cursor-pointer transition-colors ${
              scoringFormat === opt.value
                ? 'border-[#013523] bg-[#f5f0e6]'
                : 'border-[#e3dcd1] hover:border-[#c9a93a]'
            } ${locked ? 'opacity-60 cursor-not-allowed' : ''}`}
          >
            <input
              type="radio"
              name={`${testidPrefix}-fmt`}
              value={opt.value}
              checked={scoringFormat === opt.value}
              onChange={() => setScoringFormat(opt.value)}
              disabled={locked}
              className="mt-0.5"
              data-testid={`${testidPrefix}-choice-${opt.value}`}
            />
            <div className="min-w-0">
              <div className="text-sm font-semibold text-[#013523]">{opt.label}</div>
              {opt.value === 'power_set' && (
                <div className="text-[11px] text-[#6a766e] mt-0.5">
                  First to configured number of games wins the set (single set).
                </div>
              )}
              {opt.value === 'best_of_3_match_tiebreak' && (
                <div className="text-[11px] text-[#6a766e] mt-0.5">
                  Split sets → 10-point tiebreak decides the match.
                </div>
              )}
            </div>
          </label>
        ))}
      </div>

      {scoringFormat === 'power_set' && (
        <div className="grid grid-cols-2 gap-3">
          <label className="text-[11px] uppercase tracking-wider text-[#6a766e] font-medium">
            Games Target
            <input
              type="number"
              min={4} max={12}
              value={gamesTarget}
              onChange={(e) => setGamesTarget(parseInt(e.target.value, 10))}
              disabled={locked}
              data-testid={`${testidPrefix}-games-target`}
              className="mt-1 block w-full text-sm px-3 py-2 border border-[#e3dcd1] rounded-lg text-[#013523] disabled:opacity-60"
            />
          </label>
          <label className="text-[11px] uppercase tracking-wider text-[#6a766e] font-medium">
            Tiebreak To
            <input
              type="number"
              min={7} max={21}
              value={tiebreakTo}
              onChange={(e) => setTiebreakTo(parseInt(e.target.value, 10))}
              disabled={locked}
              data-testid={`${testidPrefix}-tiebreak-to`}
              className="mt-1 block w-full text-sm px-3 py-2 border border-[#e3dcd1] rounded-lg text-[#013523] disabled:opacity-60"
            />
          </label>
        </div>
      )}

      {scoringFormat === 'best_of_3_match_tiebreak' && (
        <div className="grid grid-cols-2 gap-3">
          <label className="text-[11px] uppercase tracking-wider text-[#6a766e] font-medium">
            3rd-Set Tiebreak To
            <input
              type="number"
              min={7} max={21}
              value={tiebreakTo}
              onChange={(e) => setTiebreakTo(parseInt(e.target.value, 10))}
              disabled={locked}
              data-testid={`${testidPrefix}-mtb-to`}
              className="mt-1 block w-full text-sm px-3 py-2 border border-[#e3dcd1] rounded-lg text-[#013523] disabled:opacity-60"
            />
          </label>
        </div>
      )}

      {locked ? (
        <div className="text-[11px] text-amber-800 bg-amber-50 border border-amber-100 rounded-lg px-3 py-2">
          Format of Play cannot be changed after match results have been entered.
        </div>
      ) : (
        <div className="flex justify-end">
          <button
            type="button"
            onClick={save}
            disabled={!canSave || busy}
            data-testid={`${testidPrefix}-save`}
            className="px-4 py-1.5 rounded-full bg-[#013523] text-white text-xs font-semibold uppercase tracking-[0.12em] hover:bg-[#025a3a] disabled:opacity-40"
          >
            {busy ? 'Saving…' : 'Save format'}
          </button>
        </div>
      )}
    </div>
  );
}

/* ============================== Score Fields ============================ */

// Format-aware score fields. Renders the correct inputs, computes score_text
// and winner_entry_id, and reports validity to the parent. The parent owns
// the Save button + API POST.
//
// Props:
//   event   — hydrated event ({ scoring_format, games_target, tiebreak_to })
//   entry1  — { id, display_name }
//   entry2  — { id, display_name }
//   initial — { winner_entry_id, score_text }  (for edit-existing)
//   onChange — ({ scoreText, winnerEntryId, valid, message }) => void

export function FormatAwareScoreFields({ event, entry1, entry2, initial, onChange }) {
  const fmt = event?.scoring_format || null;
  const gt = event?.games_target || 8;
  const tbTo = event?.tiebreak_to || (fmt === 'best_of_3_match_tiebreak' ? 10 : 7);

  // Power Set state
  const [psGamesA, setPsGamesA] = useState('');
  const [psGamesB, setPsGamesB] = useState('');
  const [psTbA, setPsTbA] = useState('');
  const [psTbB, setPsTbB] = useState('');
  // Best of 3 state
  const [s1a, setS1a] = useState(''); const [s1b, setS1b] = useState('');
  const [s2a, setS2a] = useState(''); const [s2b, setS2b] = useState('');
  const [mtbA, setMtbA] = useState(''); const [mtbB, setMtbB] = useState('');
  // Fallback (no structured format)
  const [plainScore, setPlainScore] = useState(initial?.score_text || '');
  const [plainWinner, setPlainWinner] = useState(initial?.winner_entry_id || '');

  // Pre-populate on edit: structured score_data first, else best-effort parse of score_text.
  useEffect(() => {
    const sd = initial?.score_data;
    if (sd && Array.isArray(sd.segments) && sd.segments.length) {
      const segs = sd.segments;
      const str = (n) => (n == null ? '' : String(n));
      if (fmt === 'power_set') {
        const g = segs.find((x) => x.type === 'power_set' || x.type === 'set');
        const tb = segs.find((x) => x.type === 'tiebreak' || x.type === 'match_tiebreak');
        if (g) { setPsGamesA(str(g.team1)); setPsGamesB(str(g.team2)); }
        if (tb) { setPsTbA(str(tb.team1)); setPsTbB(str(tb.team2)); }
      } else if (fmt === 'best_of_3_match_tiebreak') {
        const sets = segs.filter((x) => x.type === 'set');
        const mtb = segs.find((x) => x.type === 'match_tiebreak');
        if (sets[0]) { setS1a(str(sets[0].team1)); setS1b(str(sets[0].team2)); }
        if (sets[1]) { setS2a(str(sets[1].team1)); setS2b(str(sets[1].team2)); }
        if (mtb) { setMtbA(str(mtb.team1)); setMtbB(str(mtb.team2)); }
      }
      return;
    }
    if (!initial?.score_text) return;
    const s = initial.score_text.trim();
    if (fmt === 'power_set') {
      const m = s.match(/^\s*(\d+)\s*[-–]\s*(\d+)(?:\s*\(\s*(\d+)\s*[-–]\s*(\d+)\s*\))?/);
      if (m) {
        if (m[3]) {
          // Legacy "8-7 (10-7)": regulation was (N-1)-(N-1); tiebreak decided it.
          setPsGamesA(String(gt - 1)); setPsGamesB(String(gt - 1));
          setPsTbA(m[3]); setPsTbB(m[4]);
        } else {
          setPsGamesA(m[1]); setPsGamesB(m[2]);
        }
      }
    } else if (fmt === 'best_of_3_match_tiebreak') {
      // "6-4, 3-6, (10-7)"
      const parts = s.split(',').map((p) => p.trim());
      if (parts[0]) { const m = parts[0].match(/(\d+)\s*[-–]\s*(\d+)/); if (m) { setS1a(m[1]); setS1b(m[2]); } }
      if (parts[1]) { const m = parts[1].match(/(\d+)\s*[-–]\s*(\d+)/); if (m) { setS2a(m[1]); setS2b(m[2]); } }
      if (parts[2]) { const m = parts[2].match(/(\d+)\s*[-–]\s*(\d+)/); if (m) { setMtbA(m[1]); setMtbB(m[2]); } }
    } else {
      setPlainScore(initial.score_text);
      setPlainWinner(initial.winner_entry_id || '');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initial?.score_text, initial?.score_data, fmt, gt]);

  // Derive the canonical result + validity every render and notify parent.
  const result = useMemo(() => {
    if (!fmt) {
      return {
        scoreText: plainScore.trim() || null,
        winnerEntryId: plainWinner || null,
        valid: true, // legacy: no strict validation
        message: null,
      };
    }
    if (fmt === 'power_set') {
      // Power Set to N: winner reaches N with a 2-game margin (N-0 … N-(N-2)).
      // At (N-1)-(N-1) a tiebreak (to tiebreak_to, win by 2) decides the match
      // and its winner is awarded the deciding GAME → final N-(N-1).
      const a = parseInt(psGamesA, 10); const b = parseInt(psGamesB, 10);
      const bad = (message) => ({ scoreText: null, winnerEntryId: null, valid: false, message });
      if (Number.isNaN(a) || Number.isNaN(b)) return bad('Enter games for both teams.');
      if (a < 0 || b < 0 || a > gt || b > gt) return bad(`Games must be 0–${gt}.`);
      const higher = Math.max(a, b); const lower = Math.min(a, b);
      if (a === gt - 1 && b === gt - 1) {
        const ta = parseInt(psTbA, 10); const tb = parseInt(psTbB, 10);
        if (Number.isNaN(ta) || Number.isNaN(tb)) return bad(`${gt - 1}-${gt - 1}: enter the tiebreak (to ${tbTo}) to decide the match.`);
        if (ta === tb) return bad('Tiebreak cannot be tied.');
        if (Math.max(ta, tb) < tbTo) return bad(`Tiebreak must reach ${tbTo}.`);
        if (Math.abs(ta - tb) < 2) return bad('Tiebreak must be won by 2.');
        const tbWinnerA = ta > tb;
        const fa = tbWinnerA ? gt : gt - 1; const fb = tbWinnerA ? gt - 1 : gt;
        return {
          scoreText: `${fa}-${fb} (${ta}-${tb})`,
          winnerEntryId: tbWinnerA ? entry1.id : entry2.id,
          scoreData: {
            format: 'power_set',
            segments: [{ type: 'power_set', team1: a, team2: b }, { type: 'tiebreak', team1: ta, team2: tb }],
            final_games: { team1: fa, team2: fb },
          },
          valid: true, message: null,
        };
      }
      if (higher < gt) return bad(`Winner must reach ${gt} games (or enter ${gt - 1}-${gt - 1} for a tiebreak).`);
      if (a === b) return bad(`${gt}-${gt} is not possible in Power Set.`);
      if (lower === gt - 1) return bad(`${gt}-${gt - 1} is only reached through the ${gt - 1}-${gt - 1} tiebreak — enter ${gt - 1}-${gt - 1} and the tiebreak points.`);
      return {
        scoreText: `${a}-${b}`,
        winnerEntryId: a > b ? entry1.id : entry2.id,
        scoreData: { format: 'power_set', segments: [{ type: 'power_set', team1: a, team2: b }], final_games: { team1: a, team2: b } },
        valid: true, message: null,
      };
    }
    if (fmt === 'best_of_3_match_tiebreak') {
      const s1A = parseInt(s1a, 10); const s1B = parseInt(s1b, 10);
      const s2A = parseInt(s2a, 10); const s2B = parseInt(s2b, 10);
      if ([s1A, s1B, s2A, s2B].some((n) => Number.isNaN(n))) return { scoreText: null, winnerEntryId: null, valid: false, message: 'Enter Set 1 and Set 2 for both teams.' };
      const setWon = (ga, gb) => {
        if (ga === gb) return null;
        const winner = ga > gb ? 'a' : 'b';
        return winner;
      };
      const s1w = setWon(s1A, s1B);
      const s2w = setWon(s2A, s2B);
      if (!s1w || !s2w) return { scoreText: null, winnerEntryId: null, valid: false, message: 'Each set must have a winner.' };
      let scoreText = `${s1A}-${s1B}, ${s2A}-${s2B}`;
      const segments = [{ type: 'set', team1: s1A, team2: s1B }, { type: 'set', team1: s2A, team2: s2B }];
      let winnerEntryId;
      if (s1w === s2w) {
        // Straight sets — no MTB needed
        if (mtbA || mtbB) {
          return { scoreText: null, winnerEntryId: null, valid: false, message: 'Third-set tiebreak not needed in a straight-set result.' };
        }
        winnerEntryId = s1w === 'a' ? entry1.id : entry2.id;
      } else {
        // Split — need MTB
        const mA = parseInt(mtbA, 10); const mB = parseInt(mtbB, 10);
        if (Number.isNaN(mA) || Number.isNaN(mB)) return { scoreText: null, winnerEntryId: null, valid: false, message: `Split sets — enter the ${tbTo}-point deciding tiebreak.` };
        if (mA === mB) return { scoreText: null, winnerEntryId: null, valid: false, message: 'Deciding tiebreak cannot be tied.' };
        if (Math.max(mA, mB) < tbTo) return { scoreText: null, winnerEntryId: null, valid: false, message: `Deciding tiebreak must reach ${tbTo}.` };
        if (Math.abs(mA - mB) < 2 && Math.max(mA, mB) === tbTo) return { scoreText: null, winnerEntryId: null, valid: false, message: 'Deciding tiebreak must be won by 2.' };
        winnerEntryId = mA > mB ? entry1.id : entry2.id;
        scoreText = `${s1A}-${s1B}, ${s2A}-${s2B}, (${mA}-${mB})`;
        segments.push({ type: 'match_tiebreak', team1: mA, team2: mB });
      }
      return { scoreText, winnerEntryId, scoreData: { format: 'best_of_3_match_tiebreak', segments }, valid: true, message: null };
    }
    return { scoreText: null, winnerEntryId: null, valid: false, message: 'Unknown format.' };
  }, [fmt, gt, tbTo, psGamesA, psGamesB, psTbA, psTbB, s1a, s1b, s2a, s2b, mtbA, mtbB, plainScore, plainWinner, entry1, entry2]);

  useEffect(() => {
    onChange?.(result);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [result.scoreText, result.winnerEntryId, result.valid, result.message]);

  if (!fmt) {
    // Legacy fallback — same UX as before migration.
    return (
      <div className="space-y-2">
        <div className="text-[11px] uppercase tracking-wider text-[#6a766e]">Score (free text)</div>
        <input
          type="text"
          placeholder="e.g. 6-4, 6-3"
          value={plainScore}
          onChange={(e) => setPlainScore(e.target.value)}
          data-testid="score-plain-text"
          className="w-full text-sm px-3 py-2 border border-[#e3dcd1] rounded-lg"
        />
        <div className="text-[11px] uppercase tracking-wider text-[#6a766e] mt-2">Winner</div>
        <select
          value={plainWinner}
          onChange={(e) => setPlainWinner(e.target.value)}
          data-testid="score-plain-winner"
          className="w-full text-sm px-3 py-2 border border-[#e3dcd1] rounded-lg"
        >
          <option value="">Select winner…</option>
          <option value={entry1.id}>{entry1.display_name}</option>
          <option value={entry2.id}>{entry2.display_name}</option>
        </select>
        <div className="text-[11px] text-amber-700">
          This event has no structured Format of Play. Save a format on the manage page for format-aware entry.
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="text-[11px] uppercase tracking-wider text-[#6a766e]">
        {fmt === 'power_set' ? `Power Set to ${gt} · ${tbTo}-Pt Tiebreak` : `Best 2 of 3 Sets · 3rd Set ${tbTo}-Pt Match Tiebreak`}
      </div>
      <div className="grid grid-cols-[1fr_auto_1fr] gap-2 items-center text-sm">
        <TeamLines entry={entry1} align="left" />
        <div className="text-[11px] uppercase tracking-widest text-[#c9a93a] font-bold">vs</div>
        <TeamLines entry={entry2} align="right" />
      </div>

      {fmt === 'power_set' && (
        <>
          <ScoreRow
            label="Games"
            a={psGamesA} setA={setPsGamesA}
            b={psGamesB} setB={setPsGamesB}
            testid="score-power-games"
            max={gt}
          />
          {(() => {
            const a = parseInt(psGamesA, 10); const b = parseInt(psGamesB, 10);
            const showTb = a === gt - 1 && b === gt - 1;
            return showTb ? (
              <ScoreRow
                label={`${gt - 1}-${gt - 1} · Tiebreak to ${tbTo} (winner takes the match ${gt}-${gt - 1})`}
                a={psTbA} setA={setPsTbA}
                b={psTbB} setB={setPsTbB}
                testid="score-power-tb"
                max={40}
              />
            ) : null;
          })()}
        </>
      )}

      {fmt === 'best_of_3_match_tiebreak' && (
        <>
          <ScoreRow label="Set 1" a={s1a} setA={setS1a} b={s1b} setB={setS1b} testid="score-set1" max={20} />
          <ScoreRow label="Set 2" a={s2a} setA={setS2a} b={s2b} setB={setS2b} testid="score-set2" max={20} />
          {(() => {
            const a1 = parseInt(s1a, 10); const b1 = parseInt(s1b, 10);
            const a2 = parseInt(s2a, 10); const b2 = parseInt(s2b, 10);
            const bothEntered = ![a1, b1, a2, b2].some(Number.isNaN);
            const split = bothEntered && ((a1 > b1) !== (a2 > b2));
            return split ? (
              <ScoreRow
                label={`3rd Set Match Tiebreak (to ${tbTo})`}
                a={mtbA} setA={setMtbA}
                b={mtbB} setB={setMtbB}
                testid="score-mtb"
                max={40}
              />
            ) : null;
          })()}
        </>
      )}

      {result.message && (
        <div className="text-[11px] text-amber-800 bg-amber-50 border border-amber-100 rounded-lg px-3 py-2" data-testid="score-validation">
          {result.message}
        </div>
      )}
      {result.valid && result.scoreText && (
        <div className="text-[11px] text-[#015e3f]" data-testid="score-preview">
          Will save as <strong>{result.scoreText}</strong> — winner {result.winnerEntryId === entry1.id ? entry1.display_name : entry2.display_name}
        </div>
      )}
    </div>
  );
}

function TeamLines({ entry, align }) {
  const lines = entry?.team_name
    ? [entry.team_name]
    : [entry?.player1_name, entry?.player2_name].filter(Boolean);
  const shown = lines.length ? lines : [entry?.display_name || '—'];
  return (
    <div className={`min-w-0 font-semibold text-[#013523] leading-tight ${align === 'right' ? 'text-right' : ''}`} title={entry?.display_name}>
      {shown.map((l, i) => <div key={i} className="truncate">{l}</div>)}
    </div>
  );
}

// Large iPad-friendly dropdowns. Range covers extended tiebreaks (to `max`).
function ScorePick({ value, onChange, testid, max }) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      data-testid={testid}
      className="w-full h-14 text-center text-2xl font-bold tabular-nums border-2 border-[#dcd4c5] rounded-xl bg-white text-[#013523] focus:border-[#013523] focus:outline-none appearance-none"
    >
      <option value="">—</option>
      {Array.from({ length: max + 1 }, (_, n) => <option key={n} value={n}>{n}</option>)}
    </select>
  );
}

function ScoreRow({ label, a, setA, b, setB, testid, max }) {
  return (
    <div>
      <div className="text-[11px] uppercase tracking-[0.18em] text-[#013523] font-bold mb-1.5 text-center">{label}</div>
      <div className="grid grid-cols-[1fr_auto_1fr] gap-3 items-center">
        <ScorePick value={a} onChange={setA} testid={`${testid}-a`} max={max} />
        <div className="text-[10px] uppercase tracking-widest text-[#9aa39d] w-6 text-center">–</div>
        <ScorePick value={b} onChange={setB} testid={`${testid}-b`} max={max} />
      </div>
    </div>
  );
}
